import React, { useRef } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import {
  formatDateLabel,
  isTodayIso,
  shiftIsoDate,
  todayIso,
} from "./dateNavUtils.js";
import "./GlobalDateNavigator.css";

export default function GlobalDateNavigator({
  value,
  onChange,
  min,
  max,
  showTodayButton = true,
  className = "",
  ariaLabel = "Date filter",
}) {
  const inputRef = useRef(null);
  const iso = value || todayIso();
  const label = formatDateLabel(iso);
  const viewingToday = isTodayIso(iso);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.click();
  };

  const handlePick = (e) => {
    const next = String(e.target.value ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
    onChange?.(next);
  };

  const goToday = () => {
    onChange?.(todayIso());
  };

  return (
    <nav className={["gdn-date-nav", className].filter(Boolean).join(" ")} aria-label={ariaLabel}>
      <button
        type="button"
        className="gdn-date-nav__nav"
        onClick={() => onChange?.(shiftIsoDate(iso, -1))}
        aria-label="Previous day"
      >
        <ChevronLeft size={16} />
      </button>

      <button
        type="button"
        className="gdn-date-nav__label"
        onClick={openPicker}
        aria-label={`Selected date: ${label}. Click to pick a date`}
      >
        <CalendarDays size={14} aria-hidden />
        {label}
      </button>

      <input
        ref={inputRef}
        type="date"
        className="gdn-date-nav__input"
        value={iso}
        min={min}
        max={max}
        onChange={handlePick}
        tabIndex={-1}
        aria-hidden
      />

      <button
        type="button"
        className="gdn-date-nav__nav"
        onClick={() => onChange?.(shiftIsoDate(iso, 1))}
        aria-label="Next day"
      >
        <ChevronRight size={16} />
      </button>

      {showTodayButton && !viewingToday ? (
        <button type="button" className="gdn-date-nav__today" onClick={goToday}>
          Today
        </button>
      ) : null}
    </nav>
  );
}
